// kalenderPickerGruppen.ts — T-69: Gruppen der Kalenderauswahl als reine Funktionen.
// Gemeinsame Wahrheit für den Desktop-Picker (CalendarPicker) und das mobile
// Kalender-Sheet (MobileCalendarShell).
//
// Quelle ist die KALENDER-Liste (GET calendar/mailboxes → MailContext
// .calendarMailboxes). Was darin steht, darf der Nutzer lesen — Sichtbarkeit
// und Detailgrad entscheidet der Server nach Grant bzw. Outlook-Freigabe.

import type { MailboxAccess } from "../../services/graphMailService";

export interface KalenderEintrag {
    address: string;
    displayName: string;
    isOwn: boolean;
    /** Hinweis zur Outlook-Freigabe (nur Kollegen ohne Grant). */
    stufeHinweis: string | null;
}

export interface KalenderGruppen {
    /** Eigenes Postfach + Funktionspostfächer mit Grant (cbb@ …). */
    eigene: KalenderEintrag[];
    /** ALLE freigegebenen Kollegen — der Picker zeigt in der Hauptliste nur die gewählten. */
    kollegen: KalenderEintrag[];
    /** ALLE Räume — der Picker zeigt in der Hauptliste nur die gewählten. */
    raeume: KalenderEintrag[];
}

const STUFE_HINWEIS: Record<NonNullable<MailboxAccess["kalenderStufe"]>, string | null> = {
    details: null,
    titel: "nur Titel und Ort",
    belegt: "nur frei/belegt",
};

const nachName = (a: KalenderEintrag, b: KalenderEintrag) =>
    a.displayName.localeCompare(b.displayName, "de");

/** Kalender-Liste → drei Gruppen; eigener Kalender zuerst, sonst alphabetisch. */
export function gruppiereQuellen(
    liste: readonly MailboxAccess[],
    ownAddress: string | null,
): KalenderGruppen {
    const own = (ownAddress ?? "").toLowerCase();
    const gruppen: KalenderGruppen = { eigene: [], kollegen: [], raeume: [] };
    for (const m of liste) {
        if (m.calendarAccess === "NONE") continue;
        const isOwn = m.isOwn === true || (own.length > 0 && m.mailboxAddress.toLowerCase() === own);
        const eintrag: KalenderEintrag = {
            address: m.mailboxAddress,
            displayName: m.displayName || m.mailboxAddress,
            isOwn,
            stufeHinweis: m.kalenderStufe ? STUFE_HINWEIS[m.kalenderStufe] : null,
        };
        const gruppe = isOwn ? "eigene" : (m.kalenderGruppe ?? "eigene");
        if (gruppe === "kollege") gruppen.kollegen.push(eintrag);
        else if (gruppe === "raum") gruppen.raeume.push(eintrag);
        else gruppen.eigene.push(eintrag);
    }
    gruppen.eigene.sort((a, b) => (a.isOwn !== b.isOwn ? (a.isOwn ? -1 : 1) : nachName(a, b)));
    gruppen.kollegen.sort(nachName);
    gruppen.raeume.sort(nachName);
    return gruppen;
}

/** Ist die Adresse gewählt? (Groß-/Kleinschreibung egal.) */
export function istGewaehlt(address: string, selected: readonly string[]): boolean {
    const a = address.toLowerCase();
    return selected.some((s) => s.toLowerCase() === a);
}

/**
 * Räume bzw. Kollegen in der Hauptliste: nur die gewählten. Ein abgewählter
 * Eintrag verschwindet wieder daraus (Wunsch aus dem Ticket: „wenn wieder
 * abgewählt nicht da anzeigen“); hinzufügen geht über die Unterauswahl.
 */
export function nurGewaehlte(
    eintraege: readonly KalenderEintrag[],
    selected: readonly string[],
): KalenderEintrag[] {
    return eintraege.filter((r) => istGewaehlt(r.address, selected));
}

export const sichtbareRaeume = nurGewaehlte;
export const sichtbareKollegen = nurGewaehlte;

const norm = (s: string) =>
    s
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "");

/** Suche über Name und Adresse (Räume, Kollegen); leere Eingabe ⇒ alle. */
export function filterEintraege(
    eintraege: readonly KalenderEintrag[],
    query: string,
): KalenderEintrag[] {
    const q = norm(query.trim());
    if (!q) return [...eintraege];
    return eintraege.filter((r) => norm(r.displayName).includes(q) || norm(r.address).includes(q));
}

export const filterRaumEintraege = filterEintraege;

/** Zahl der gewählten Kalender, die es in der Liste (noch) gibt — für das Badge. */
export function anzahlGewaehlt(gruppen: KalenderGruppen, selected: readonly string[]): number {
    return [...gruppen.eigene, ...gruppen.kollegen, ...gruppen.raeume].filter((e) =>
        istGewaehlt(e.address, selected),
    ).length;
}
